import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
    Loader2,
    ArrowLeft,
    ShieldAlert,
    FileText,
    Target,
    ListChecks,
    ArrowRight,
    Scale,
    FileSignature,
    FileSearch,
    Download,
    Sparkles,
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import {
    getCase,
    getMeta,
    listProducts,
    purchaseProduct,
    getCaseDocuments,
} from "@/lib/api";

const STATUS_LABEL = {
    submitted: "QUEUED",
    analyzing: "ANALYZING — 10 BOTS RUNNING",
    analyzed: "ANALYSIS COMPLETE",
};

const ScoreGauge = ({ score }) => {
    const s = score ?? 0;
    const color = s >= 80 ? "#FFFFFF" : s >= 50 ? "#FFCC00" : "#FF3B30";
    return (
        <div className="border-2 border-white/20 p-8" data-testid="score-gauge">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-4">
                CASE STRENGTH SCORE
            </div>
            <div className="flex items-end gap-6">
                <div className="font-anton leading-none" style={{ color, fontSize: "clamp(5rem, 12vw, 10rem)" }}>
                    {s}
                </div>
                <div className="font-mono text-sm text-white/50 pb-3">/ 100</div>
            </div>
            <div className="w-full h-2 bg-white/10 mt-4">
                <div
                    className="h-full transition-all duration-700"
                    style={{ width: `${s}%`, background: color }}
                />
            </div>
            <div className="font-mono text-xs uppercase tracking-wider mt-4">
                {s >= 80 ? "STRONG CASE — PROCEED" : s >= 50 ? "MODERATE — FORTIFY EVIDENCE" : "WEAK — REINFORCE BEFORE FILING"}
            </div>
        </div>
    );
};

const ListBlock = ({ title, items, icon: Icon, accent }) => (
    <div className="border-2 border-white/20 p-6">
        <div className="flex items-center gap-3 mb-4">
            <Icon size={18} className={accent ? "text-[#FF3B30]" : "text-white"} />
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">{title}</div>
        </div>
        {items?.length ? (
            <ul className="space-y-2">
                {items.map((it, i) => (
                    <li key={i} className="font-sans text-sm text-white/85 flex gap-3">
                        <span className="font-mono text-xs text-[#FF3B30] mt-0.5">{String(i + 1).padStart(2, "0")}</span>
                        <span>{it}</span>
                    </li>
                ))}
            </ul>
        ) : (
            <div className="font-mono text-xs text-white/40 uppercase tracking-wider">— NONE IDENTIFIED —</div>
        )}
    </div>
);

export default function Analysis() {
    const { id } = useParams();
    const [c, setC] = useState(null);
    const [meta, setMeta] = useState(null);
    const [activeBot, setActiveBot] = useState(null);
    const [polling, setPolling] = useState(true);
    const [products, setProducts] = useState([]);
    const [docs, setDocs] = useState([]);
    const [purchasing, setPurchasing] = useState(null);
    const [activeDoc, setActiveDoc] = useState(null);

    useEffect(() => {
        getMeta().then(setMeta).catch(() => {});
        listProducts()
            .then((d) => setProducts(d.products || []))
            .catch(() => {});
    }, []);

    useEffect(() => {
        if (!c?.user_email) {
            getCaseDocuments(id)
                .then((d) => setDocs(d.documents || []))
                .catch(() => {});
        } else {
            getCaseDocuments(id, c.user_email)
                .then((d) => setDocs(d.documents || []))
                .catch(() => {});
        }
    }, [id, c?.user_email]);

    const buyProduct = async (productKey) => {
        setPurchasing(productKey);
        try {
            const r = await purchaseProduct({
                case_id: id,
                product_key: productKey,
                user_email: c?.user_email || "",
                origin_url: window.location.origin,
            });
            window.location.href = r.url;
        } catch (e) {
            toast.error("Could not start checkout");
            setPurchasing(null);
        }
    };

    useEffect(() => {
        let timer;
        const tick = async () => {
            try {
                const data = await getCase(id);
                setC(data);
                if (!activeBot && data?.bot_reports) {
                    const firstKey = Object.keys(data.bot_reports)[0];
                    if (firstKey) setActiveBot(firstKey);
                }
                if (data?.status === "analyzed") {
                    setPolling(false);
                    return;
                }
            } catch (e) {
                /* keep polling */
            }
            timer = setTimeout(tick, 3000);
        };
        tick();
        return () => clearTimeout(timer);
    }, [id, activeBot]);

    if (!c) {
        return (
            <div className="bg-[#0a0a0a] min-h-screen text-white">
                <Header />
                <div className="max-w-[1200px] mx-auto px-6 py-32 text-center">
                    <Loader2 size={32} className="animate-spin mx-auto text-[#FF3B30]" />
                    <div className="font-mono text-xs uppercase tracking-[0.3em] text-white/50 mt-6">
                        LOADING CASE...
                    </div>
                </div>
            </div>
        );
    }

    const bots = meta?.bots || [];
    const activeReport = activeBot ? c.bot_reports?.[activeBot] : null;
    const activeBotMeta = bots.find((b) => b.key === activeBot);

    return (
        <div className="bg-[#0a0a0a] min-h-screen text-white">
            <Header />

            <div className="max-w-[1600px] mx-auto px-6 sm:px-10 py-10">
                <Link
                    to="/hpm-dashboard"
                    className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 hover:text-white flex items-center gap-2 mb-6"
                    data-testid="back-to-dashboard"
                >
                    <ArrowLeft size={14} /> BACK TO MY CASES
                </Link>

                {/* Header bar */}
                <div className="border-2 border-white/20 p-6 mb-8" data-testid="case-header">
                    <div className="flex flex-wrap items-baseline justify-between gap-4">
                        <div>
                            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-2">
                                CASE / {c.id.slice(0, 8).toUpperCase()}
                            </div>
                            <h1 className="font-anton text-3xl sm:text-5xl uppercase leading-none">
                                {c.case_title}
                            </h1>
                            <div className="font-mono text-xs text-white/60 mt-3 flex flex-wrap gap-x-6 gap-y-1">
                                <span>TYPE: {c.case_type}</span>
                                <span>JURISDICTION: {c.jurisdiction}</span>
                                <span>LANG: {c.language}</span>
                            </div>
                        </div>
                        <div
                            className={`font-mono text-xs uppercase tracking-[0.25em] px-3 py-2 border-2 ${
                                c.status === "analyzed"
                                    ? "border-white text-white"
                                    : "border-[#FF3B30] text-[#FF3B30] animate-pulse"
                            }`}
                            data-testid="case-status"
                        >
                            {STATUS_LABEL[c.status] || c.status}
                        </div>
                    </div>
                </div>

                {/* Analyzing state */}
                {c.status !== "analyzed" && (
                    <div className="border-2 border-white/20 p-12 text-center mb-8" data-testid="analyzing-block">
                        <Loader2 size={32} className="animate-spin mx-auto text-[#FF3B30]" />
                        <div className="font-anton text-3xl uppercase mt-6">10 BOTS ENGAGED</div>
                        <div className="font-mono text-xs uppercase tracking-[0.3em] text-white/60 mt-2">
                            CLAUDE SONNET 4.5 // PARALLEL EXECUTION // TYPICALLY 30–90s
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-8 max-w-3xl mx-auto">
                            {bots.map((b) => (
                                <div
                                    key={b.key}
                                    className="border border-white/15 p-3 font-mono text-[10px] uppercase tracking-wider text-white/70"
                                >
                                    {b.code}
                                    <div className="text-white/40 text-[9px] mt-1">{b.name}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Analyzed */}
                {c.status === "analyzed" && (
                    <>
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                            <div className="lg:col-span-2">
                                <ScoreGauge score={c.strength_score} />
                            </div>
                            <div className="border-2 border-white/20 p-6">
                                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-4">
                                    STRATEGY SUMMARY
                                </div>
                                <p className="font-sans text-sm text-white/85 leading-relaxed">
                                    {c.analysis_summary || "Strategy summary unavailable."}
                                </p>
                            </div>
                        </div>

                        {/* Attorney Escalation CTA */}
                        <div
                            className="border-2 border-[#FF3B30] bg-[#FF3B30]/5 p-6 mb-8 flex flex-col md:flex-row md:items-center justify-between gap-6"
                            data-testid="attorney-cta"
                        >
                            <div className="flex items-start gap-4">
                                <Scale size={28} className="text-[#FF3B30] flex-shrink-0 mt-1" />
                                <div>
                                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/60 mb-1">
                                        STEP NEXT — TAKE IT TO COURT
                                    </div>
                                    <div className="font-anton text-2xl uppercase leading-none">
                                        Get matched with specialist attorneys.
                                    </div>
                                    <div className="font-sans text-sm text-white/70 mt-2 max-w-xl">
                                        Verified attorneys in your jurisdiction with proven track records on cases like yours.
                                        Pro-bono availability flagged. Free for you — firms pay HPM, not you.
                                    </div>
                                </div>
                            </div>
                            <Link
                                to={`/attorney-escalation/${c.id}`}
                                className="btn-brutal btn-brutal-red flex-shrink-0"
                                data-testid="match-attorney-cta"
                            >
                                MATCH ME <ArrowRight size={16} />
                            </Link>
                        </div>

                        {/* PREMIUM DOCUMENTS */}
                        <div className="border-2 border-white/20 mb-8" data-testid="premium-docs-panel">
                            <div className="flex items-center gap-3 p-4 border-b-2 border-white/20">
                                <Sparkles size={16} className="text-[#FFCC00]" />
                                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/60">
                                    PREMIUM DOCUMENTS · AI-GENERATED · COURT-READY
                                </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3">
                                {products.map((p, i) => {
                                    const IconMap = {
                                        premium_report: FileSearch,
                                        demand_letter: FileSignature,
                                        evidence_request_pack: FileText,
                                    };
                                    const Icon = IconMap[p.key] || FileText;
                                    const alreadyOwned = docs.some((d) => d.product_key === p.key);
                                    return (
                                        <div
                                            key={p.key}
                                            className="p-6 border-white/20 md:[&:not(:last-child)]:border-r-2 [&:not(:last-child)]:border-b-2 md:[&:not(:last-child)]:border-b-0"
                                            data-testid={`product-${p.key}`}
                                        >
                                            <Icon size={24} className="text-[#FF3B30] mb-3" />
                                            <div className="font-anton text-xl uppercase leading-tight mb-1">
                                                {p.label}
                                            </div>
                                            <div className="font-anton text-3xl text-white mt-1 leading-none">
                                                ${p.price.toFixed(0)}
                                            </div>
                                            <p className="font-sans text-xs text-white/70 mt-3 leading-relaxed">
                                                {p.blurb}
                                            </p>
                                            {alreadyOwned ? (
                                                <div className="mt-4 font-mono text-[10px] uppercase tracking-[0.25em] text-white border-2 border-white px-3 py-2 text-center">
                                                    ✓ DELIVERED — SEE BELOW
                                                </div>
                                            ) : (
                                                <button
                                                    onClick={() => buyProduct(p.key)}
                                                    disabled={purchasing === p.key}
                                                    className="btn-brutal btn-brutal-red w-full justify-center text-[10px] mt-4"
                                                    data-testid={`buy-${p.key}`}
                                                >
                                                    {purchasing === p.key ? (
                                                        <Loader2 size={12} className="animate-spin" />
                                                    ) : (
                                                        <>BUY NOW</>
                                                    )}
                                                </button>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* DELIVERED DOCUMENTS */}
                        {docs.length > 0 && (
                            <div className="border-2 border-white/20 mb-8" data-testid="delivered-docs">
                                <div className="flex items-center gap-3 p-4 border-b-2 border-white/20">
                                    <Download size={16} className="text-white" />
                                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/60">
                                        YOUR DOCUMENTS ({docs.length})
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-3 border-b-2 border-white/20">
                                    {docs.map((d, i) => (
                                        <button
                                            key={d.document_id}
                                            onClick={() => setActiveDoc(d)}
                                            data-testid={`doc-tab-${d.product_key}`}
                                            className={`p-4 text-left border-white/20 md:[&:not(:nth-child(3n))]:border-r-2 [&:not(:last-child)]:border-b-2 md:[&:nth-child(-n+3)]:border-b-0 transition-colors ${
                                                activeDoc?.document_id === d.document_id
                                                    ? "bg-white text-black"
                                                    : "hover:bg-white/5 text-white"
                                            }`}
                                        >
                                            <div className="font-mono text-[9px] uppercase tracking-[0.25em] opacity-70">
                                                {(d.created_at || "").slice(0, 10)}
                                            </div>
                                            <div className="font-anton text-base uppercase leading-tight mt-1">
                                                {d.product_label}
                                            </div>
                                        </button>
                                    ))}
                                </div>
                                {activeDoc && (
                                    <pre className="p-6 font-mono text-xs text-white/90 whitespace-pre-wrap leading-relaxed max-h-[600px] overflow-y-auto">
{activeDoc.content}
                                    </pre>
                                )}
                            </div>
                        )}

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
                            <ListBlock title="VIOLATIONS IDENTIFIED" items={c.violations_identified} icon={ShieldAlert} accent />
                            <ListBlock title="EVIDENCE GAPS" items={c.evidence_gaps} icon={FileText} accent />
                            <ListBlock title="PRECEDENTS FOUND" items={c.precedents_found} icon={Target} />
                            <ListBlock title="RECOMMENDED ACTIONS" items={c.recommended_actions} icon={ListChecks} />
                        </div>

                        {/* Bot tabs */}
                        <div className="border-2 border-white/20 mb-10" data-testid="bot-reports">
                            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 p-4 border-b-2 border-white/20">
                                INDIVIDUAL BOT REPORTS
                            </div>
                            <div className="grid grid-cols-2 sm:grid-cols-5 border-b-2 border-white/20">
                                {bots.map((b) => (
                                    <button
                                        key={b.key}
                                        onClick={() => setActiveBot(b.key)}
                                        data-testid={`bot-tab-${b.key}`}
                                        className={`p-3 text-left border-white/20 [&:not(:nth-child(5n))]:border-r-2 [&:nth-child(-n+5)]:border-b-2 sm:[&:nth-child(-n+5)]:border-b-0 transition-colors ${
                                            activeBot === b.key
                                                ? "bg-white text-black"
                                                : "hover:bg-white/5 text-white"
                                        }`}
                                    >
                                        <div className="font-mono text-[9px] uppercase tracking-[0.2em] opacity-70">
                                            {b.code}
                                        </div>
                                        <div className="font-anton text-sm uppercase leading-tight mt-1">
                                            {b.name}
                                        </div>
                                    </button>
                                ))}
                            </div>

                            {activeReport && activeBotMeta && (
                                <div className="p-6">
                                    <div className="flex items-baseline justify-between mb-4">
                                        <div>
                                            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">
                                                {activeBotMeta.code}
                                            </div>
                                            <div className="font-anton text-3xl uppercase">{activeBotMeta.name}</div>
                                        </div>
                                        <div className="font-anton text-5xl text-[#FF3B30]">
                                            {activeReport.score}
                                        </div>
                                    </div>
                                    <p className="font-sans text-sm text-white/85 leading-relaxed mb-5">
                                        {activeReport.findings}
                                    </p>
                                    {activeReport.key_points?.length > 0 && (
                                        <div>
                                            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-2">
                                                KEY POINTS
                                            </div>
                                            <ul className="space-y-2">
                                                {activeReport.key_points.map((p, i) => (
                                                    <li key={i} className="font-sans text-sm text-white/85 flex gap-3">
                                                        <span className="font-mono text-xs text-[#FF3B30]">→</span>
                                                        <span>{p}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </>
                )}

                {/* Submission echo */}
                <div className="border-2 border-white/20 p-6" data-testid="case-submission-echo">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-3">
                        SUBMISSION
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <div className="font-mono text-[10px] uppercase text-white/40 mb-1">PARTIES</div>
                            <div className="font-sans text-sm">
                                <span className="text-white/60">Plaintiff:</span> {c.parties_plaintiff}
                            </div>
                            <div className="font-sans text-sm">
                                <span className="text-white/60">Defendant:</span> {c.parties_defendant}
                            </div>
                        </div>
                        <div>
                            <div className="font-mono text-[10px] uppercase text-white/40 mb-1">EVIDENCE</div>
                            <div className="font-mono text-xs text-white/70">
                                {c.evidence_submitted?.length || 0} ITEMS LISTED
                            </div>
                        </div>
                    </div>
                    <div className="mt-4">
                        <div className="font-mono text-[10px] uppercase text-white/40 mb-1">DESCRIPTION</div>
                        <p className="font-sans text-sm text-white/80 leading-relaxed">{c.description}</p>
                    </div>
                </div>
            </div>

            <Footer disclaimer={meta?.disclaimer || ""} />
        </div>
    );
}
